<template>
  <Modal v-model="dialogShow">
    <!-- scrollable keeps the adjustment list and the footer buttons reachable with long previews -->
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <h1 class="mb-1">{{ $t('publication.editor.adjustments.load_title') }}</h1>
          <p class="text-muted mb-4">
            {{
              isSingle
                ? $t('publication.editor.adjustments.load_description_single')
                : $t('publication.editor.adjustments.load_description')
            }}
          </p>

          <div class="adjustment-list mb-3">
            <div
              v-for="adjustment in adjustments"
              :key="adjustment.id"
              class="adjustment-row"
              :class="{ selected: adjustment.id === selectedId }"
              @click="selectedId = adjustment.id">
              <input
                class="form-check-input m-0"
                type="radio"
                :checked="adjustment.id === selectedId"
                @change="selectedId = adjustment.id" />
              <span class="adjustment-row-name">{{ adjustment.name }}</span>
              <LeadButton
                variant="icon"
                class="adjustment-row-delete"
                :aria-label="$t('btn.delete')"
                @click.stop="requestDelete(adjustment)">
                <IconX :size="16" />
              </LeadButton>
            </div>
          </div>

          <div v-if="selectedAdjustment" class="adjustment-preview">
            <div class="adjustment-preview-title text-muted">
              {{ $t('publication.editor.adjustments.preview_title') }}
            </div>
            <div v-for="entry in previewEntries" :key="entry.key" class="adjustment-preview-row">
              <span class="adjustment-preview-label text-muted">{{ entry.label }}</span>
              <span class="adjustment-preview-value">
                <AdjustmentValuePreview v-if="entry.def" :def="entry.def" :value="entry.value" />
                <span v-else class="text-muted">{{ entry.fallback }}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <LeadButton variant="white" @click="decline">
            {{ $t('publication.editor.adjustments.start_from_defaults') }}
          </LeadButton>
          <LeadButton variant="primary" :disabled="!selectedAdjustment" @click="load">
            {{ $t('publication.editor.adjustments.load') }}
          </LeadButton>
        </div>
      </div>
    </div>
  </Modal>

  <LiConfirmationDialog
    v-model:dialogShow="showDeleteConfirm"
    :confirmButtonLabel="$t('btn.delete')"
    @onContinue="confirmDelete">
    <h1 class="mb-3">{{ $t('publication.editor.adjustments.delete_confirm_title') }}</h1>
    <p>{{ $t('publication.editor.adjustments.delete_confirm_description') }}</p>
  </LiConfirmationDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ConfirmationDialog as LiConfirmationDialog, Modal } from '@prospective/lithium';
import { IconX } from '@tabler/icons-vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import AdjustmentValuePreview from './AdjustmentValuePreview.vue';
import type { IPublicationAdjustment } from '@/types/publicationAdjustment';
import type { TemplateOptionDef } from '@/types/templateMeta';
import { formatOptionValue, optionLabel } from './adjustmentHelpers';

const props = defineProps<{
  adjustments: IPublicationAdjustment[];
  optionDefs: TemplateOptionDef[];
}>();

const dialogShow = defineModel<boolean>('dialogShow', { default: false });

const emit = defineEmits<{
  load: [adjustment: IPublicationAdjustment];
  delete: [adjustment: IPublicationAdjustment];
  decline: [];
}>();

/* --------------------------------- STATE ---------------------------------- */
const selectedId = ref<number | null>(null);
const showDeleteConfirm = ref(false);
const pendingDelete = ref<IPublicationAdjustment | null>(null);

/* -------------------------------- COMPUTED -------------------------------- */
const isSingle = computed(() => props.adjustments.length === 1);
const selectedAdjustment = computed(() => props.adjustments.find((a) => a.id === selectedId.value) ?? null);

const previewEntries = computed(() => {
  const values = selectedAdjustment.value?.values ?? {};
  return Object.keys(values).map((key) => ({
    key,
    label: optionLabel(props.optionDefs, key),
    def: props.optionDefs.find((d) => d.properties.key === key) ?? null,
    value: values[key],
    fallback: formatOptionValue(values[key]),
  }));
});

/* -------------------------------- WATCHERS -------------------------------- */
// preselect the first (or only) adjustment whenever the dialog opens or the list changes
watch(
  [dialogShow, () => props.adjustments],
  () => {
    if (dialogShow.value && props.adjustments.length && selectedAdjustment.value === null) {
      selectedId.value = props.adjustments[0].id;
    }
  },
  { immediate: true }
);

/* --------------------------------- METHODS -------------------------------- */
function load() {
  if (!selectedAdjustment.value) return;
  emit('load', selectedAdjustment.value);
  dialogShow.value = false;
}

function decline() {
  emit('decline');
  dialogShow.value = false;
}

function requestDelete(adjustment: IPublicationAdjustment) {
  pendingDelete.value = adjustment;
  showDeleteConfirm.value = true;
}

function confirmDelete() {
  if (pendingDelete.value) {
    emit('delete', pendingDelete.value);
    pendingDelete.value = null;
  }
  showDeleteConfirm.value = false;
}
</script>

<style scoped>
.adjustment-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--tblr-border-color, #eaecf0);
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.adjustment-row:last-child {
  margin-bottom: 0;
}

.adjustment-row.selected {
  border-color: var(--tblr-primary, #206bc4);
}

.adjustment-row-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* the delete affordance only appears on hover of its row */
.adjustment-row-delete {
  flex-shrink: 0;
  visibility: hidden;
}

.adjustment-row:hover .adjustment-row-delete,
.adjustment-row-delete:focus-visible {
  visibility: visible;
}

.adjustment-preview {
  border-top: 1px solid var(--tblr-border-color, #eaecf0);
  padding-top: 12px;
}

.adjustment-preview-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
}

/* stacked so rich values (benefit lists, color swatches) have room to render */
.adjustment-preview-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
}

.adjustment-preview-label {
  font-size: 0.75rem;
}

.adjustment-preview-value {
  min-width: 0;
}
</style>
